import { useEffect } from "react";
import reportsService from "api/reports/reports.service";
import { AppDispatch, RootState, useAppDispatch, useAppSelector } from "store";
import { sharedReportsFailed, sharedReportsReceived, sharedReportsRequested } from "store/reportsSlice";

/**
 * Loads the reports shared with the user's team into `state.reports`. Without `force` it fetches only when this user's
 * list was never requested, so every caller can ask freely: Layout remounts on navigation, StrictMode double-invokes.
 * The continuation writes only while its request is still the latest (`[SELECTION_ANCHOR_BY_IDENTITY]`).
 */
export const loadSharedReports =
  (userId: string, { force = false }: { force?: boolean } = {}) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    if (!force && getState().reports.loadedFor === userId) return;
    dispatch(sharedReportsRequested(userId));
    const requestId = getState().reports.requestId;
    const isCurrent = () => getState().reports.requestId === requestId;

    try {
      const res = await reportsService.getSharedReports();
      if (!isCurrent()) return;
      // A handled 401/403 resolves to `{ _handled: true }`, not an array (`[HTTP_HANDLED_RESPONSE]`).
      dispatch(Array.isArray(res) ? sharedReportsReceived(res) : sharedReportsFailed());
    } catch {
      if (isCurrent()) dispatch(sharedReportsFailed());
    }
  };

/**
 * Mounted in Layout: fetches the shared reports once the session is authorized, once per user. Gated on
 * `isAuthorized` because an inactive account answers 402, and the interceptor would put the paywall popup up on
 * every page for a list the menu only reads.
 */
export const useSharedReportsBoot = () => {
  const dispatch = useAppDispatch();
  const isAuthorized = useAppSelector((s) => s.common.isAuthorized);
  const userId = useAppSelector((s) => s.common.user.id);

  useEffect(() => {
    if (!isAuthorized || !userId) return;
    dispatch(loadSharedReports(String(userId)));
  }, [isAuthorized, userId, dispatch]);
};
